import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing video mute state with intersection observer
 * Ensures only one video is unmuted at a time and mutes when off-screen
 * 
 * @param {string} videoId - Unique identifier for this video
 * @returns {Object} - { isMuted, toggleMute, videoRef, isInView }
 */
const useVideoMute = (videoId) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const videoRef = useRef(null);

  // Listen for global unmute events from other videos
  useEffect(() => {
    const handleOtherVideoUnmute = (event) => {
      if (event.detail.videoId !== videoId && !isMuted) {
        setIsMuted(true);
      }
    };

    window.addEventListener('videoUnmuted', handleOtherVideoUnmute);
    return () => {
      window.removeEventListener('videoUnmuted', handleOtherVideoUnmute);
    };
  }, [videoId, isMuted]);

  // Intersection Observer to detect when video goes off-screen
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        
        // Mute video when it goes off-screen
        if (!entry.isIntersecting && !isMuted) {
          setIsMuted(true);
        }
      },
      {
        threshold: 0.5, // Video must be at least 50% visible
        rootMargin: '0px'
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [isMuted]);

  // Toggle mute function
  const toggleMute = useCallback(() => {
    if (isMuted) {
      // Dispatch event to mute all other videos
      window.dispatchEvent(new CustomEvent('videoUnmuted', { 
        detail: { videoId } 
      }));
    }
    setIsMuted(!isMuted);
  }, [isMuted, videoId]);

  return {
    isMuted,
    toggleMute,
    videoRef,
    isInView
  };
};

export default useVideoMute;
